import { BadgeWorkOrderStatus } from 'components/BadgeWorgOrderStatus';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { HEADER_HEIGHT } from 'components/Layout/Master';
import type { ContractChangeStatusEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { AgreementsStatus } from 'pages/Project/UerProject/AgreementsRegistry/AgreementsStatus';
import { WorkOrderChangeStatus } from 'pages/Project/UerProject/WorkOrderChange/WorkOrderChangeStatus';
import {
    Breadcrumbs,
    Button,
    cnMixSpace,
    IconAlert,
    IconArrowDown,
    IconArrowLeft,
    IconArrowRight,
    IconArrowUp,
} from 'uikit';

import * as R from 'ramda';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Split from 'react-split';

import { ProjectTree } from '../ProjectTree';

import { PageHeaderLayoutContext, PageLeftSplitLayoutContext } from './PageLayoutContext';

interface IProjectPageLayoutProps {
    title: string;
    breadCrumbsItems?: any[];
    style?: CSSProperties;
    headerStyle?: CSSProperties;
    statusWorkOrder?: WorkOrderStatusEnum;
    statusWorkOrderChange?: WorkOrderChangeStatusEnum;
    statusContractChange?: ContractChangeStatusEnum;
    headerRightSide?: ReactNode | string;
    headerLeftSide?: ReactNode | string;
    headerBottom?: ReactNode | string;
    isWorkOrderChangeRequired?: boolean;
    isHeaderCollapsable?: boolean;
    layoutEntityKey?: string | number;
    isHasBadge?: boolean;
    classNameHeader?: string | number;
    children?: ReactNode;
}

export const ProjectPageLayout = (props: IProjectPageLayoutProps) => {
    const navigate = useNavigate();

    const { isHeaderOpened, setIsHeaderOpened } = useContext(PageHeaderLayoutContext);
    const { isLeftPanelOpened, setIsLeftPanelOpened } = useContext(PageLeftSplitLayoutContext);

    const {
        title,
        children,
        breadCrumbsItems = [],
        style,
        headerStyle,
        statusWorkOrder,
        statusContractChange,
        statusWorkOrderChange,
        headerRightSide,
        headerLeftSide,
        headerBottom,
        isWorkOrderChangeRequired,
        isHeaderCollapsable,
        isHasBadge = true,
        classNameHeader,
    } = props;

    const pages = useMemo(
        () => [
            {
                label: 'Реестр проектов',
                href: '/projects',
            },
            ...breadCrumbsItems,
            {
                label: title,
            },
        ],
        [breadCrumbsItems, title],
    );

    const handleBreadcrumbsItemClick = useCallback(
        ({ e, item }) => {
            e.preventDefault();
            navigate(item.href);
        },
        [navigate],
    );

    const handlePanelOpen = useCallback(() => {
        setIsLeftPanelOpened(R.not);
    }, [setIsLeftPanelOpened]);

    const handleHeaderOpen = useCallback(() => {
        setIsHeaderOpened(R.not);
    }, [setIsHeaderOpened]);

    const splitElementStyle = useCallback(
        (
            dimension: 'width' | 'height',
            elementSize: number,
            gutterSize: number,
            index: number,
        ): Partial<CSSStyleDeclaration> => {
            if (!isLeftPanelOpened) {
                return index === 0
                    ? {
                          width: '16px',
                      }
                    : {
                          width: `calc(100% - ${gutterSize || 16}px)`,
                      };
            }

            return {
                width: `calc(${elementSize}% - ${gutterSize || 16}px)`,
            };
        },
        [isLeftPanelOpened],
    );

    const headerCollapseStyle: CSSProperties = useMemo(
        () => ({
            position: 'absolute',
            top: 0,
            left: isLeftPanelOpened ? -10 : 0,
            width: isLeftPanelOpened ? 'calc(100% + 10px)' : '100%',
            height: 16,
            maxWidth: 'unset',
        }),
        [isLeftPanelOpened],
    );

    const getComponentBadge = () => {
        if (statusWorkOrder === WorkOrderStatusEnum.InProgress && isWorkOrderChangeRequired) {
            return (
                <ComponentWithTooltip
                    tooltipText="Содержит несогласованные изменения"
                    status="warning"
                    styles={{ display: 'inline-flex', margin: '-2px 4px 0 0', verticalAlign: 'middle' }}
                >
                    <IconAlert
                        size="s"
                        view="warning"
                        style={{
                            width: 20,
                            height: 20,
                        }}
                    />
                </ComponentWithTooltip>
            );
        }

        if (statusWorkOrder) {
            return (
                <BadgeWorkOrderStatus size="m" status={statusWorkOrder} style={{ margin: '0 4px -4px 0' }} as="span" />
            );
        }

        return null;
    };

    return (
        <Split
            className="split"
            sizes={[15, 85]}
            gutterSize={isLeftPanelOpened ? 10 : 0}
            minSize={isLeftPanelOpened ? [300, 800] : [16, 1200]}
            elementStyle={splitElementStyle}
            style={{
                alignItems: 'stretch',
                minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            }}
        >
            <div
                className="First"
                style={{
                    borderRight: '1px solid var(--color-bg-border)',
                    position: 'relative',
                    backgroundColor: isLeftPanelOpened ? 'white' : 'var(--color-control-bg-ghost)',
                    padding: '12px 0',
                    overflow: 'auto',
                }}
            >
                {isLeftPanelOpened && <ProjectTree />}
                <Button
                    view="ghost"
                    iconRight={isLeftPanelOpened ? IconArrowLeft : IconArrowRight}
                    onlyIcon
                    onClick={handlePanelOpen}
                    size="s"
                    form="brick"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: 16,
                    }}
                />
            </div>
            <div style={{ position: 'relative' }}>
                {isHeaderCollapsable && (
                    <Button
                        view="ghost"
                        iconRight={isHeaderOpened ? IconArrowUp : IconArrowDown}
                        onlyIcon
                        onClick={handleHeaderOpen}
                        size="s"
                        form="brick"
                        style={headerCollapseStyle}
                    />
                )}
                <section className={cnMixSpace({ m: 'xl' })} style={style}>
                    {(!isHeaderCollapsable || isHeaderOpened) && (
                        <header
                            className={classNameHeader ? `${classNameHeader}` : undefined}
                            style={{
                                ...headerStyle,
                                ...(isHeaderCollapsable ? { marginTop: 32 } : {}),
                            }}
                        >
                            <Breadcrumbs items={pages} onItemClick={handleBreadcrumbsItemClick} size="xs" />
                            <h2>
                                {isHasBadge && getComponentBadge()}
                                {statusContractChange && (
                                    <AgreementsStatus
                                        isMinified
                                        statusKey={statusContractChange}
                                        style={{ margin: '0 4px -4px 0' }}
                                    />
                                )}
                                {statusWorkOrderChange && (
                                    <WorkOrderChangeStatus
                                        isMinified
                                        statusKey={statusWorkOrderChange}
                                        style={{ margin: '0 4px -4px 0' }}
                                    />
                                )}
                                {headerLeftSide}
                                {title}
                                {headerRightSide}
                            </h2>
                            {headerBottom}
                        </header>
                    )}
                    {children}
                </section>
            </div>
        </Split>
    );
};

ProjectPageLayout.displayName = 'ProjectPageLayout';
